/**
 * Web Audio player for tracks that are still being generated.
 *
 * A plain <audio> element needs a complete file up front. This schedules short
 * slices of a Float32Array a fraction of a second ahead of the playhead, so a
 * track can be played while later parts of it are still being computed -- and
 * so switching between tracks is sample-accurate rather than a reload.
 */

export interface TrackData {
  ch0: Float32Array
  ch1: Float32Array
  /** How many samples of ch0/ch1 are actually valid so far. */
  ready: number
}

/** Slice length to schedule at a time. Small enough that a track switch feels instant. */
const SLICE_SEC = 0.15
/** How far ahead of the playhead to stay scheduled. Covers timer jitter. */
const LOOKAHEAD_SEC = 0.35
const TIMER_MS = 50

export class StreamingPlayer {
  private ctx: AudioContext | null = null
  private tracks = new Map<string, TrackData>()
  private sources = new Set<AudioBufferSourceNode>()
  private timer: number | null = null

  private activeId: string | null = null
  private playing = false
  /** Next sample index to schedule. Runs ahead of what is audible. */
  private cursor = 0
  /** AudioContext time at which `cursor` will be heard. */
  private nextTime = 0

  /** Total length in samples, from the longest track. Set by `setTrack`. */
  private totalSamples = 0

  constructor(
    readonly sampleRate: number,
    private readonly onChange: () => void,
  ) {}

  private audio(): AudioContext {
    if (!this.ctx) this.ctx = new AudioContext({ sampleRate: this.sampleRate })
    return this.ctx
  }

  setTrack(id: string, data: TrackData): void {
    this.tracks.set(id, data)
    this.totalSamples = Math.max(this.totalSamples, data.ch0.length)
    if (!this.activeId) this.activeId = id
    this.onChange()
  }

  hasTrack(id: string): boolean {
    return this.tracks.has(id)
  }

  reset(): void {
    this.stopSources()
    this.tracks.clear()
    this.activeId = null
    this.playing = false
    this.cursor = 0
    this.totalSamples = 0
    this.onChange()
  }

  get active(): string | null {
    return this.activeId
  }

  get isPlaying(): boolean {
    return this.playing
  }

  get duration(): number {
    return this.totalSamples / this.sampleRate
  }

  /** How much of the active track is playable, in seconds. */
  get readyTime(): number {
    const t = this.activeId ? this.tracks.get(this.activeId) : null
    return t ? t.ready / this.sampleRate : 0
  }

  /** Playhead in seconds -- what the listener is hearing, not what is scheduled. */
  get currentTime(): number {
    if (!this.playing || !this.ctx) return this.cursor / this.sampleRate
    const aheadSec = Math.max(0, this.nextTime - this.ctx.currentTime)
    const audible = this.cursor - aheadSec * this.sampleRate
    return Math.max(0, audible) / this.sampleRate
  }

  play(): void {
    if (this.playing) return
    const ctx = this.audio()
    void ctx.resume()
    this.playing = true
    this.nextTime = ctx.currentTime + 0.06
    this.startTimer()
    this.onChange()
  }

  pause(): void {
    if (!this.playing) return
    // Freeze at what is actually audible, discarding anything already queued.
    this.cursor = Math.round(this.currentTime * this.sampleRate)
    this.playing = false
    this.stopSources()
    this.stopTimer()
    this.onChange()
  }

  seek(seconds: number): void {
    const target = Math.max(0, Math.min(seconds, this.duration))
    this.cursor = Math.round(target * this.sampleRate)
    if (this.playing) {
      this.stopSources()
      this.nextTime = this.audio().currentTime + 0.06
    }
    this.onChange()
  }

  /** Switch source track, keeping the playhead where it is. */
  switchTo(id: string): void {
    if (!this.tracks.has(id) || id === this.activeId) return
    const at = this.currentTime
    this.activeId = id
    if (this.playing) {
      this.cursor = Math.round(at * this.sampleRate)
      this.stopSources()
      this.nextTime = this.audio().currentTime + 0.06
    }
    this.onChange()
  }

  dispose(): void {
    this.stopSources()
    this.stopTimer()
    void this.ctx?.close()
    this.ctx = null
  }

  private startTimer(): void {
    if (this.timer !== null) return
    this.timer = window.setInterval(() => this.pump(), TIMER_MS)
    this.pump()
  }

  private stopTimer(): void {
    if (this.timer === null) return
    window.clearInterval(this.timer)
    this.timer = null
  }

  private stopSources(): void {
    for (const s of this.sources) {
      try {
        s.stop()
      } catch {
        // Already ended; nothing to do.
      }
    }
    this.sources.clear()
  }

  /** Top up the scheduling queue so it stays LOOKAHEAD_SEC ahead of playback. */
  private pump(): void {
    if (!this.playing || !this.activeId) return
    const ctx = this.audio()
    const track = this.tracks.get(this.activeId)
    if (!track) return

    const slice = Math.round(SLICE_SEC * this.sampleRate)

    while (this.nextTime < ctx.currentTime + LOOKAHEAD_SEC) {
      if (this.cursor >= this.totalSamples) {
        // Reached the end of the song.
        this.playing = false
        this.stopTimer()
        this.onChange()
        return
      }
      // Underrun: processing hasn't caught up with playback yet. Wait for more.
      if (this.cursor >= track.ready) {
        // Re-anchor so we don't schedule into the past once data arrives.
        this.nextTime = Math.max(this.nextTime, ctx.currentTime + 0.06)
        return
      }

      const end = Math.min(this.cursor + slice, track.ready, this.totalSamples)
      const len = end - this.cursor
      if (len <= 0) return

      const buf = ctx.createBuffer(2, len, this.sampleRate)
      buf.getChannelData(0).set(track.ch0.subarray(this.cursor, end))
      buf.getChannelData(1).set(track.ch1.subarray(this.cursor, end))

      const src = ctx.createBufferSource()
      src.buffer = buf
      src.connect(ctx.destination)
      src.start(this.nextTime)
      src.onended = () => this.sources.delete(src)
      this.sources.add(src)

      this.nextTime += len / this.sampleRate
      this.cursor = end
    }
  }
}
